import { PurchasePriceSummary, DeferredVariablePaymentType, CorporateEquityType } from '../../../../../slices/marketplace/loi-form/term-form';
import type { DetailColumnDef, DetailRow } from '../../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiAndTermsForm/TermsFormDetailBlock';
import {
    DFP_COLUMN_DEFS,
    DVP_COLUMN_DEFS,
    JV_COLUMN_DEFS,
    CORP_COMMON_COLUMN_DEFS,
    CORP_PREFERRED_COLUMN_DEFS,
    dfpRowToKeyValue,
    dvpRowToKeyValue,
    jvRowToKeyValue,
    corpCommonRowToKeyValue,
    corpPreferredRowToKeyValue,
    totalDfp,
    totalDvp,
} from '../../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiAndTermsForm/termsFormDetailColumns';

export interface DeepDiveDetailViewProps {
    title: string;
    columns: DetailColumnDef[];
    rows: DetailRow[];
    displayTotal?: number | null;
    showTotalRow?: boolean;
    singleRow?: boolean;
    showPaymentNumber?: boolean;
    paymentNumberHeader?: string;
}

export type DeepDiveBuilder = (summary: PurchasePriceSummary | null | undefined) => DeepDiveDetailViewProps;

export const DEEP_DIVE_REGISTRY: Record<string, DeepDiveBuilder | undefined> = {
    'Cash at Close': undefined,
    'Deferred Fixed Payments': (summary) => ({
        title: 'Deferred Fixed Payments',
        columns: DFP_COLUMN_DEFS,
        rows: (summary?.deferredFixedPayments?.rows ?? []).map(dfpRowToKeyValue),
        displayTotal: totalDfp(summary?.deferredFixedPayments?.rows ?? []),
        showTotalRow: true,
        showPaymentNumber: true,
        paymentNumberHeader: '',
    }),
    'Earnout': (summary) => {
        const filtered = (summary?.deferredVariablePayments?.rows ?? [])
            .filter((r) => r.type === DeferredVariablePaymentType.Earnout);
        return {
            title: 'Earnout',
            columns: DVP_COLUMN_DEFS,
            rows: filtered.map(dvpRowToKeyValue),
            displayTotal: totalDvp(filtered),
            showTotalRow: true,
            showPaymentNumber: true,
            paymentNumberHeader: '',
        };
    },
    'Synthetic JV Equity': (summary) => {
        const filtered = (summary?.deferredVariablePayments?.rows ?? [])
            .filter((r) => r.type === DeferredVariablePaymentType.Synthetic);
        return {
            title: 'Synthetic JV Equity',
            columns: DVP_COLUMN_DEFS,
            rows: filtered.map(dvpRowToKeyValue),
            displayTotal: totalDvp(filtered),
            showTotalRow: true,
            showPaymentNumber: true,
            paymentNumberHeader: '',
        };
    },
    'JV Equity': (summary) => ({
        title: 'JV Equity',
        columns: JV_COLUMN_DEFS,
        rows: summary?.jvEquity?.row ? [jvRowToKeyValue(summary.jvEquity.row)] : [],
        displayTotal: Number(summary?.jvEquity?.row?.valueAtClosing) || 0,
        showTotalRow: true,
        singleRow: true,
    }),
    'Corporate Equity': undefined,
    'Common Corp Equity': (summary) => {
        const row = (summary?.corporateEquity?.rows ?? []).find((r) => r.type === CorporateEquityType.Common);
        return {
            title: 'Common Corp Equity',
            columns: CORP_COMMON_COLUMN_DEFS,
            rows: row ? [corpCommonRowToKeyValue(row)] : [],
            displayTotal: Number(row?.valueAtClosing) || 0,
            showTotalRow: true,
            singleRow: true,
        };
    },
    'Preferred Corp Equity': (summary) => {
        const row = (summary?.corporateEquity?.rows ?? []).find((r) => r.type === CorporateEquityType.Preferred);
        return {
            title: 'Preferred Corp Equity',
            columns: CORP_PREFERRED_COLUMN_DEFS,
            rows: row ? [corpPreferredRowToKeyValue(row)] : [],
            displayTotal: Number(row?.valueAtClosing) || 0,
            showTotalRow: true,
            singleRow: true,
        };
    },
    'Other': undefined,
};

export const getFirstDeepDiveRowLabel = (
    summary: PurchasePriceSummary | null | undefined,
): string | null => {
    for (const [label, builder] of Object.entries(DEEP_DIVE_REGISTRY)) {
        if (!builder) {
            continue;
        }
        if ((builder(summary).displayTotal ?? 0) > 0) {
            return label;
        }
    }
    return null;
};
